/**
 * useLeadDetail.ts — fetch + refetch state for a single lead's detail (LS8).
 *
 * Owns the request lifecycle for `GET /api/leads/{id}` and exposes a `refetch`
 * the write UI calls after a successful transition (so the new status + the
 * freshly-appended history entry appear without a full reload).
 *
 * Phases map the BFF's distinct read failures to distinct UI states (spec
 * §Error Handling): 401/403 → `unauthorized` (the clean screen); a 404 →
 * `notFound` (the lead is gone / not visible — NOT a generic error); the BFF's
 * 502 "source unavailable" or any transport/5xx → `error` with retry. Race
 * safety mirrors useLeadsQuery: a monotonic request id means only the LATEST
 * fetch may commit, and a response resolving after unmount is a no-op.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import type { LeadDetail } from '../../api/types';
import floating from '../../lib/floating';

export type LeadDetailPhase = 'loading' | 'ready' | 'unauthorized' | 'notFound' | 'error';

export interface LeadDetailState {
  detail: LeadDetail | null;
  phase: LeadDetailPhase;
  /** Re-run the detail fetch (used after a successful transition). */
  refetch: () => Promise<void>;
}

export const useLeadDetail = (apiClient: ApiClient, leadId: string): LeadDetailState => {
  const [detail, setDetail] = useState<LeadDetail | null>(null);
  const [phase, setPhase] = useState<LeadDetailPhase>('loading');

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchDetail = useCallback(async () => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const isCurrent = () => mountedRef.current && seq === requestSeq.current;
    setPhase('loading');
    try {
      const payload = await apiClient.getLeadDetail(leadId);
      if (!isCurrent()) return;
      setDetail(payload);
      setPhase('ready');
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof UnauthorizedError) {
        setPhase('unauthorized');
        return;
      }
      if (error instanceof ApiError && error.status === 404) {
        setPhase('notFound');
        return;
      }
      // BFF 502 (source unavailable) / network / 5xx — keep any last-known
      // detail on screen but flag the error so the page can offer a retry.
      setPhase('error');
    }
  }, [apiClient, leadId]);

  // Fetch on mount and whenever the lead id changes (hash navigation).
  useEffect(() => {
    floating(fetchDetail());
  }, [fetchDetail]);

  return { detail, phase, refetch: fetchDetail };
};
